import { dayLabel, formatMinutes } from '@kronos/domain'
import type { DayLine } from '@kronos/domain'
import { Badge } from '../primitives'
import { clockTime } from './time'
import type { ShiftEdit } from './ShiftEditor'

/**
 * One day of one person's shifts: a row inside a panel, with the times, what
 * they signed off, and a button beside anything that needs fixing. Shared by
 * My hours and People › Hours, so a person and an engineer see a day the same
 * way.
 */
export function DayCard({ day, who, onEdit }: { day: DayLine; who?: string; onEdit: (edit: ShiftEdit) => void }): JSX.Element {
  const work = day.work.map((w) => (w.count > 1 ? `${w.count} × ${w.label}` : w.label)).join(', ')
  const attention = day.workWithoutShift || day.shifts.some((s) => s.forgotten)

  return (
    <div className={`day-card${attention ? ' day-card--attention' : ''}`}>
      <div className="day-card__head">
        <strong>{dayLabel(day.date)}</strong>
        <span className="figure">{day.shifts.length > 0 ? formatMinutes(day.minutes) : '—'}</span>
      </div>

      {day.shifts.map((s) => (
        <div className="day-card__shift" key={s.id}>
          <span className="muted">
            {clockTime(s.startedAt)} – {s.endedAt ? clockTime(s.endedAt) : s.forgotten ? 'no clock-out' : 'now'}
          </span>
          <span className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto' }}>
            {s.onShift ? <Badge tone="healthy">On shift</Badge> : null}
            {s.edited ? <Badge>Typed in</Badge> : null}
            {s.forgotten ? <Badge tone="warning">Not counted</Badge> : null}
            {!s.onShift ? (
              <button
                className={s.forgotten ? 'primary' : undefined}
                onClick={() => onEdit({ kind: 'edit', id: s.id, startedAt: s.startedAt, endedAt: s.endedAt, who })}
              >
                {s.forgotten ? 'Set clock-out' : 'Fix'}
              </button>
            ) : null}
          </span>
        </div>
      ))}

      {day.workWithoutShift ? (
        <div className="day-card__shift">
          <span className="warn">Signed off work, but never clocked in.</span>
          <button className="primary" onClick={() => onEdit({ kind: 'add', who })}>
            Add the shift
          </button>
        </div>
      ) : null}

      {work ? <span className="muted day-card__work">{work}</span> : null}
    </div>
  )
}
